import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { registerAudio } from '../lib/audioManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Robot,
  PaperPlaneRight,
  Microphone,
  MicrophoneSlash,
  SpeakerHigh,
  SpeakerSlash,
  Trash,
  Gear,
  X,
  Spinner,
  Waveform,
  Stop
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVATARS = {
  professional: { gradient: 'from-[#5B8C9A] to-[#4A7A87]', label: 'Prof.' },
  friendly: { gradient: 'from-[#AB1C24] to-[#8A151C]', label: 'Freundlich' },
  creative: { gradient: 'from-[#8b5cf6] to-[#7c3aed]', label: 'Kreativ' },
};

const AITeacherPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState({
    name: 'Profesora María',
    personality: 'encouraging',
    avatar_style: 'professional',
    voice_gender: 'female'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(null);
  const [playingIdx, setPlayingIdx] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const currentAudioRef = useRef(null);
  const audioCacheRef = useRef({});
  const ttsLoadingRef = useRef(false);

  useEffect(() => {
    loadHistory();
    loadSettings();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/ai-teacher/history?limit=50`, { withCredentials: true });
      setMessages(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingHistory(false); }
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/ai-teacher/settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (e) { console.error(e); }
  };

  const saveSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/ai-teacher/settings`, newSettings, { withCredentials: true });
      setSettings(newSettings);
      toast.success('Einstellungen gespeichert');
      setShowSettings(false);
    } catch (e) { toast.error('Fehler beim Speichern'); }
  };

  const stopCurrentAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setPlayingIdx(null);
  }, []);

  const playAudio = useCallback(async (text, msgIndex) => {
    // Guard: if something is already playing/loading, stop it first to prevent overlap
    stopCurrentAudio();
    if (ttsLoadingRef.current) return; // avoid parallel TTS requests
    const cacheKey = text.substring(0, 100);
    if (audioCacheRef.current[cacheKey]) {
      const audio = registerAudio(new Audio(audioCacheRef.current[cacheKey]));
      currentAudioRef.current = audio;
      setPlayingIdx(msgIndex ?? 'latest');
      audio.onended = () => { currentAudioRef.current = null; setPlayingIdx(null); };
      audio.onerror = () => { currentAudioRef.current = null; setPlayingIdx(null); };
      audio.play().catch(() => setPlayingIdx(null));
      return;
    }
    ttsLoadingRef.current = true;
    setTtsLoading(msgIndex ?? true);
    try {
      const res = await axios.post(`${API}/ai-teacher/tts`, { text }, { withCredentials: true });
      if (res.data.audio_base64) {
        const dataUrl = `data:audio/mp3;base64,${res.data.audio_base64}`;
        audioCacheRef.current[cacheKey] = dataUrl;
        // Re-check: make sure no other audio started while we were fetching
        stopCurrentAudio();
        const audio = registerAudio(new Audio(dataUrl));
        currentAudioRef.current = audio;
        setPlayingIdx(msgIndex ?? 'latest');
        audio.onended = () => { currentAudioRef.current = null; setPlayingIdx(null); };
        audio.onerror = () => { currentAudioRef.current = null; setPlayingIdx(null); };
        audio.play().catch(() => setPlayingIdx(null));
      }
    } catch (e) {
      console.error('TTS error:', e);
    } finally {
      ttsLoadingRef.current = false;
      setTtsLoading(null);
    }
  }, [stopCurrentAudio]);

  const sendTextMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    stopCurrentAudio();

    const optimisticMsg = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      const res = await axios.post(`${API}/ai-teacher/chat`, {
        message: text,
        request_exercise: false
      }, { withCredentials: true });
      const teacherMsg = {
        role: 'assistant',
        content: res.data.content,
        teacher_name: res.data.teacher_name,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, teacherMsg]);
      if (autoSpeak) playAudio(res.data.content);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const msg = typeof detail === 'string' ? detail
        : !status ? 'Verbindungsfehler. Bitte Internetverbindung prüfen.'
        : status === 401 ? 'Sitzung abgelaufen. Bitte neu anmelden.'
        : status === 429 ? 'Zu viele Anfragen. Kurz warten.'
        : `Fehler ${status}`;
      toast.error(msg);
    } finally { setSending(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a supported mimeType
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
          : MediaRecorder.isTypeSupported('audio/ogg') ? 'audio/ogg' : '';
      }
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(recordingIntervalRef.current);
        setRecordingTime(0);

        if (audioChunksRef.current.length === 0) return;
        const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        await processVoiceMessage(blob, ext);
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (e) {
      console.error('Microphone error:', e);
      toast.error('Mikrofon nicht verfügbar. Bitte erlaube den Zugriff.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processVoiceMessage = async (blob, ext = 'webm') => {
    setVoiceProcessing(true);
    stopCurrentAudio();
    const formData = new FormData();
    formData.append('file', blob, `voice.${ext}`);

    const tempId = `voice_${Date.now()}`;
    const optimistic = { _tempId: tempId, role: 'user', content: '...', is_voice: true, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);

    try {
      const res = await axios.post(`${API}/ai-teacher/voice-chat`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      // Replace optimistic msg with actual transcription
      setMessages(prev => {
        const updated = prev.map(m => m._tempId === tempId ? { ...m, content: res.data.user_text, _tempId: undefined } : m);
        return [...updated, {
          role: 'assistant',
          content: res.data.content,
          teacher_name: res.data.teacher_name,
          is_voice: true,
          created_at: new Date().toISOString()
        }];
      });

      // Play response audio from the voice-chat response (already generated, no extra TTS call)
      if (res.data.audio_base64) {
        const dataUrl = `data:audio/mp3;base64,${res.data.audio_base64}`;
        const cacheKey = res.data.content.substring(0, 100);
        audioCacheRef.current[cacheKey] = dataUrl;
        stopCurrentAudio();
        const audio = registerAudio(new Audio(dataUrl));
        currentAudioRef.current = audio;
        setPlayingIdx('latest');
        audio.onended = () => { currentAudioRef.current = null; setPlayingIdx(null); };
        audio.onerror = () => { currentAudioRef.current = null; setPlayingIdx(null); };
        audio.play().catch(() => setPlayingIdx(null));
      }
    } catch (e) {
      console.error('Voice processing error:', e);
      toast.error('Fehler bei der Sprachverarbeitung');
      setMessages(prev => prev.filter(m => m._tempId !== tempId));
    } finally { setVoiceProcessing(false); }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/ai-teacher/history`, { withCredentials: true });
      setMessages([]);
      toast.success('Verlauf gelöscht');
    } catch (e) { toast.error('Fehler'); }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const avatarConfig = AVATARS[settings.avatar_style] || AVATARS.professional;

  return (
    <div className="h-[calc(100vh-7rem)] lg:h-[calc(100vh-8rem)] flex flex-col" data-testid="ai-teacher-page">
      {/* Top Bar */}
      <div className="flex items-center justify-between bg-white rounded-2xl border border-[#D6D3D1] p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${avatarConfig.gradient} flex items-center justify-center`}>
            <Robot size={24} className="text-white" weight="fill" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg text-[#292524]">{settings.name}</h2>
            <span className="text-sm text-[#78716C]">KI-Spanischlehrerin</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(playingIdx !== null || ttsLoading !== null) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={stopCurrentAudio}
              className="text-[#AB1C24] hover:bg-[#AB1C24]/10 animate-pulse"
              title="Audio stoppen"
              data-testid="stop-audio-btn"
            >
              <Stop size={18} weight="fill" className="mr-1.5" />
              <span className="text-sm font-medium">Stopp</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoSpeak(!autoSpeak)}
            className={autoSpeak ? 'text-[#AB1C24]' : 'text-[#78716C]'}
            title={autoSpeak ? 'Stimme an' : 'Stimme aus'}
            data-testid="toggle-auto-speak"
          >
            {autoSpeak ? <SpeakerHigh size={20} weight="fill" /> : <SpeakerSlash size={20} />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} data-testid="settings-btn">
            <Gear size={20} />
          </Button>
          <Button variant="ghost" size="sm" onClick={clearHistory} data-testid="clear-history-btn">
            <Trash size={20} />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-white rounded-2xl border border-[#D6D3D1] p-4 mb-4 space-y-4" data-testid="messages-area">
        {loadingHistory ? (
          <div className="flex items-center justify-center h-full">
            <Spinner size={32} className="animate-spin text-[#AB1C24]" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${avatarConfig.gradient} flex items-center justify-center mb-4`}>
              <Robot size={40} className="text-white" weight="fill" />
            </div>
            <h3 className="font-heading font-bold text-xl text-[#292524] mb-2">
              ¡Hola! Ich bin {settings.name}
            </h3>
            <p className="text-[#78716C] max-w-md mb-6">
              Sprich mit mir auf Spanisch! Du kannst tippen oder das Mikrofon nutzen.
              Ich helfe dir bei Grammatik, Vokabeln und Aussprache.
            </p>
            <div className="flex gap-3 flex-wrap justify-center">
              {['¿Cómo estás?', '¿Qué tiempo hace?', 'Necesito ayuda con el subjuntivo'].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="px-4 py-2 bg-[#F5F5F4] rounded-full text-sm text-[#57534E] hover:bg-[#D6D3D1] transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${avatarConfig.gradient} flex items-center justify-center flex-shrink-0`}>
                  <Robot size={18} className="text-white" weight="fill" />
                </div>
              )}
              <div className={`max-w-[75%] ${msg.role === 'user'
                ? 'bg-[#AB1C24] text-white rounded-2xl rounded-br-md'
                : 'bg-[#F5F5F4] text-[#292524] rounded-2xl rounded-bl-md'
              } px-4 py-3`}>
                {msg.is_voice && msg.role === 'user' && (
                  <div className="flex items-center gap-1.5 mb-1 opacity-70">
                    <Microphone size={12} />
                    <span className="text-xs">Sprachnachricht</span>
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.role === 'assistant' && (
                  <button
                    onClick={() => playingIdx === i ? stopCurrentAudio() : playAudio(msg.content, i)}
                    className="mt-2 p-1.5 rounded-lg hover:bg-black/10 transition-colors flex items-center gap-1.5"
                    title={playingIdx === i ? 'Stop' : 'Vorlesen'}
                    disabled={ttsLoading === i}
                    data-testid={`speak-msg-${i}`}
                  >
                    {ttsLoading === i ? (
                      <><Spinner size={14} className="animate-spin text-[#AB1C24]" /><span className="text-xs text-[#AB1C24]">Generando...</span></>
                    ) : playingIdx === i ? (
                      <><Stop size={16} className="text-[#AB1C24]" weight="fill" /><span className="text-xs text-[#AB1C24]">Stop</span></>
                    ) : (
                      <SpeakerHigh size={16} className={msg.role === 'user' ? 'text-white/70' : 'text-[#78716C]'} />
                    )}
                  </button>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-9 h-9 rounded-xl bg-[#AB1C24]/20 flex items-center justify-center flex-shrink-0 text-[#AB1C24] font-bold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white rounded-2xl border border-[#D6D3D1] p-3" data-testid="input-area">
        {isRecording ? (
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              <Waveform size={24} className="text-red-500 animate-pulse" />
              <span className="text-red-600 font-medium">{formatTime(recordingTime)}</span>
            </div>
            <Button
              onClick={stopRecording}
              className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6"
              data-testid="stop-recording-btn"
            >
              <Stop size={20} weight="fill" className="mr-2" />
              Stop
            </Button>
          </div>
        ) : voiceProcessing ? (
          <div className="flex items-center justify-center gap-3 px-4 py-3">
            <Spinner size={20} className="animate-spin text-[#AB1C24]" />
            <span className="text-[#78716C]">Generando respuesta...</span>
          </div>
        ) : (
          <form onSubmit={sendTextMessage} className="flex items-center gap-2">
            <Button
              type="button"
              onClick={startRecording}
              variant="ghost"
              className="rounded-full w-11 h-11 p-0 text-[#AB1C24] hover:bg-[#AB1C24]/10 flex-shrink-0"
              title="Spracheingabe"
              data-testid="start-recording-btn"
            >
              <Microphone size={22} weight="fill" />
            </Button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe en español..."
              className="flex-1 border-0 bg-[#F5F5F4] rounded-xl focus-visible:ring-1 focus-visible:ring-[#AB1C24]"
              disabled={sending}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || sending}
              className="bg-[#AB1C24] hover:bg-[#8A151C] rounded-full w-11 h-11 p-0 flex-shrink-0"
              data-testid="send-btn"
            >
              {sending ? <Spinner size={18} className="animate-spin" /> : <PaperPlaneRight size={20} weight="fill" />}
            </Button>
          </form>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="settings-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">Lehrereinstellungen</h3>
              <button onClick={() => setShowSettings(false)}><X size={20} /></button>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#292524] mb-1.5">Name</label>
              <Input
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                data-testid="settings-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#292524] mb-1.5">Persönlichkeit</label>
              <Select value={settings.personality} onValueChange={(v) => setSettings({ ...settings, personality: v })}>
                <SelectTrigger data-testid="settings-personality">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="encouraging">Ermutigend</SelectItem>
                  <SelectItem value="strict">Streng</SelectItem>
                  <SelectItem value="playful">Spielerisch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#292524] mb-1.5">Avatar-Stil</label>
              <Select value={settings.avatar_style} onValueChange={(v) => setSettings({ ...settings, avatar_style: v })}>
                <SelectTrigger data-testid="settings-avatar">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professionell</SelectItem>
                  <SelectItem value="friendly">Freundlich</SelectItem>
                  <SelectItem value="creative">Kreativ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#292524] mb-1.5">Stimme</label>
              <Select value={settings.voice_gender || 'female'} onValueChange={(v) => setSettings({ ...settings, voice_gender: v })}>
                <SelectTrigger data-testid="settings-voice">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Weiblich (Nova)</SelectItem>
                  <SelectItem value="male">Männlich (Onyx)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => saveSettings(settings)}
              className="w-full bg-[#AB1C24] hover:bg-[#8A151C]"
              data-testid="save-settings-btn"
            >
              Speichern
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITeacherPage;
